<script setup lang="ts">
/** The cut list, linked to the model: rows select parts (aria-current), hover mirrors the viewer. */
import { computed } from 'vue';
import type { BoardAssembly } from '@/families/assembly';
import { fmtMm } from '@/domain';
import { ui } from '@/app/state';
import { hover, select } from '@/app/navigation';
import { grainArrow, grainText } from '@/app/format';

const props = withDefaults(defineProps<{ build: Pick<BoardAssembly, 'parts'>; compact?: boolean; link?: boolean; caption?: string; partIds?: string[] | null }>(), { compact: false, link: true, caption: 'Cut list: finished sizes in millimetres', partIds: null });
const rows = computed(() => (props.partIds ? props.build.parts.filter((p) => props.partIds!.includes(p.id)) : props.build.parts));

function toggle(id: string) { select(ui.sel === id ? null : id); }
function onKey(e: KeyboardEvent, id: string) {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(id); return; }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const rows = [...((e.currentTarget as HTMLElement).closest('tbody')?.querySelectorAll<HTMLElement>('[data-part-row]') ?? [])];
    const i = rows.indexOf(e.currentTarget as HTMLElement);
    rows[(i + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length]?.focus();
  }
}
function over(id: string) { if (props.link && id !== ui.hov) hover(id); }
</script>

<template>
  <table class="cut" :class="{ 'cut--compact': compact }" @pointerleave="link && hover(null)">
    <caption class="sr-only">{{ caption }}</caption>
    <thead>
      <tr>
        <th scope="col">Part</th>
        <th v-if="!compact" scope="col" class="num">Qty</th>
        <th scope="col" class="num">L × W × T</th>
        <th scope="col">Grain</th>
        <th v-if="!compact" scope="col" class="col-edge">Edge</th>
        <th v-if="!compact && rows.some((p) => p.glueUp)" scope="col">Glue-up</th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="p in rows"
        :key="p.id"
        :data-part-row="link ? p.id : undefined"
        :tabindex="link ? 0 : undefined"
        :class="link ? { 'is-sel': ui.sel === p.id, 'is-hov': ui.hov === p.id && ui.sel !== p.id } : undefined"
        :aria-current="link && ui.sel === p.id ? 'true' : undefined"
        @click="link && toggle(p.id)"
        @keydown="link && onKey($event, p.id)"
        @pointerover="over(p.id)"
        @focus="link && hover(p.id)"
      >
        <th scope="row"><span class="pid-row"><span class="mono pid">{{ p.id }}</span></span><span class="plabel">{{ p.label }}</span></th>
        <td v-if="!compact" class="num">{{ p.qty }}</td>
        <td class="num mono">{{ fmtMm(p.finished.l) }} × {{ fmtMm(p.finished.w) }} × {{ fmtMm(p.finished.t) }}</td>
        <td><span aria-hidden="true" class="garrow">{{ grainArrow(p) }}</span> {{ grainText(p) }}</td>
        <td v-if="!compact" class="col-edge">{{ p.edge }}</td>
        <td v-if="!compact && rows.some((x) => x.glueUp)">{{ p.glueUp ? `${p.glueUp.strips} strips of ${fmtMm(p.glueUp.stripWidthTicks)}` : '—' }}</td>
      </tr>
    </tbody>
  </table>
</template>
